import { useParams, useNavigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import useStudents, { type StudentData } from '@hooks/useStudents'
import type { Student } from '@services/contribution/contribution'
import useOnboarding from '@/hooks/useOnboarding'

import Section from '@components/Section'
import AddOrEditStudentForm from '@components/AddOrEditStudentForm'
import Spinner from '@components/Spinner'
import {
  sendStudentEditedEvent,
  sendStudentEditErrorEvent,
  sendStudentDeletedEvent,
  sendStudentDeleteErrorEvent,
} from '@/services/analytics/gtm'

function findStudentById(studentId: number, students: Student[] | undefined) {
  if (!studentId || !students) {
    return null
  }
  return students?.find((student) => student.id === studentId)
}

function formatUpdateStudentError(errorMessage: string): string {
  const lowerMessage = errorMessage.toLowerCase()
  if (lowerMessage.includes('404')) {
    return 'Student could not be updated because it does not exist.'
  }
  if (lowerMessage.includes('first name and last name')) {
    return 'A student with this first and last name already exists.'
  }
  return errorMessage || 'Sorry! We were unable to update this student. Please try again.'
}

function formatDeleteStudentError(errorMessage: string): string {
  const lowerMessage = errorMessage.toLowerCase()
  if (lowerMessage.includes('404')) {
    return 'Student already deleted.'
  }
  return errorMessage || 'Sorry! We were unable to delete this student. Please try again.'
}
export default function ManageStudentPage() {
  const { t } = useTranslation()
  const { isOnboarding } = useOnboarding()
  const navigate = useNavigate()
  const { studentId } = useParams()
  const { students, isLoading, updateStudent, deleteStudent } = useStudents()
  const student = findStudentById(Number(studentId), students)

  async function handleUpdateStudent(data: StudentData) {
    try {
      await updateStudent({
        student: {
          id: studentId,
          firstName: data.firstName,
          lastName: data.lastName,
          classroomId: data.classroomId,
        },
      })
      sendStudentEditedEvent({ isOnboarding: isOnboarding ?? false })
      toast.success(t('content.manage_student_page.update_success_toast'))
      navigate(isOnboarding ? '../onboarding' : '../account', { replace: true })
      return true
    } catch (error) {
      sendStudentEditErrorEvent({ isOnboarding: isOnboarding ?? false })
      Sentry.logger.error('Error updating student', { error: String(error) })
      const rawErrorMessage =
        error instanceof Error ? error.message : 'Sorry! We were unable to update this student. Please try again.'
      const formattedMessage = formatUpdateStudentError(rawErrorMessage)
      toast.error(formattedMessage, {
        autoClose: false,
      })
      return false
    }
  }

  async function handleDeleteStudent() {
    try {
      if (!studentId) throw new Error('Student Id is required to delete.')

      await deleteStudent({ studentId })
      sendStudentDeletedEvent({
        isOnboarding: isOnboarding ?? false,
        studentCount: (students?.length ?? 1) - 1,
      })
      Sentry.logger.debug(Sentry.logger.fmt`Delete student ${studentId}`)
      toast.success(t('content.manage_student_page.delete_success_toast'))
      navigate(isOnboarding ? '../onboarding' : '../account', { replace: true })
      return true
    } catch (error) {
      sendStudentDeleteErrorEvent({ isOnboarding: isOnboarding ?? false })
      const rawErrorMessage =
        error instanceof Error ? error.message : 'Sorry! We were unable to delete this student. Please try again.'
      const formattedMessage = formatDeleteStudentError(rawErrorMessage)
      Sentry.logger.error('Error deleting student', { error: String(error) })
      toast.error(formattedMessage, {
        autoClose: false,
      })
      return false
    }
  }

  if (isLoading) {
    return (
      <div className='flex flex-col items-center mx-auto py-56'>
        <div className='text-xl font-serif text-white mb-4 '> {t('content.manage_student_page.loading')}</div>
        <Spinner />
      </div>
    )
  }

  if (!student) {
    return (
      <div className='flex flex-row justify-center mx-auto py-24'>
        <div className='text-xl font-serif text-white mb-4 text-center'>
          {t('content.manage_student_page.not_found')}
        </div>
      </div>
    )
  }

  return (
    <>
      <title>{t('content.manage_student_page.page_title')}</title>
      <Section title={t('content.manage_student_page.section_title')} wrapperStyle='mt-8 mb-4'>
        <div className='text-2xl font-bold text-white text-center'>{t('content.manage_student_page.header')}</div>
        <AddOrEditStudentForm onSubmit={handleUpdateStudent} onDelete={handleDeleteStudent} student={student} />
      </Section>
    </>
  )
}
